import * as React from 'react';

import type { FieldVM } from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';
import type WorkSummaryDetailVM from 'ab-viewModels/fieldReport/workSummaryDetails.viewModel';

import { resolveToLargerUnit } from '@acceligentllc/shared/utils/compoundUnit';

import { QuantityUnitLabel, QuantityUnitMap } from '@acceligentllc/shared/enums/quantityUnit';
import * as ReportBlockFieldEnum from '@acceligentllc/shared/enums/reportBlockField';

import { bemBlock, bemElement } from 'af-utils/bem.util';
import type { CellWidthsMap } from 'af-utils/react.util';

import Checkbox from 'af-components/Controls/Checkbox';
import Dropdown from 'af-components/Controls/Dropdown';

import WorkSummaryDetailsTableCell from './WorkSummaryDetailsTableCell';
import BillingTypeIndicator from '../BillingTypeIndicator';
import { resolveHighlightGroupKey } from '../helpers';
import styles from './styles.module.scss';
import { resolveFieldValue } from '../Table/helpers';
import { WORK_SUMMARY_COLUMN_DEFINITION_MAP, createIndexedColumn } from '../values';

interface Props {
	data: WorkSummaryDetailVM;
	maxNumberOfDefinitionFields: number;
	maxNumberOfInformationalFields: number;
	onBillingStatusToggle: (workSummaryDetail: WorkSummaryDetailVM) => Promise<void>;
	onRowHighlight: (groupToHighlight: Nullable<string>, groupKeyToHighlight: Nullable<string>) => void;
	highlightedGroup: Nullable<string | number>;
	cellWidthsMap: CellWidthsMap;
	isReadOnly: boolean;
	hasPreviouslyBeenSplit: boolean;
	isGroupAndExcludeActionDisabled: boolean;
	isFirstOfItsGroupKey: boolean;
	isFirstOfItsGroupName: boolean;
	isLastOfItsGroupName: boolean;
	isOfCurrentlyHighlightedGroup: boolean;
	checkedWorkSummaryDetails: WorkSummaryDetailVM[];
	setNewWorkSummaryCheckedList: (wsds: WorkSummaryDetailVM[]) => void;
	onGroupTogetherClick: () => Promise<void>;
	onRestoreOriginalClick: () => Promise<void>;
	onExcludeFromCalculationGroupClick: () => Promise<void>;
	currentlyHighlightedGroupKey: Nullable<string>;
	jumpToQuantityFieldPathName: string;
	isAlternativeOption: boolean;
	isCustomSorted: boolean;
	timeZone: Nullable<string>;
}

const _fillWithEmptyFields = (fields: FieldVM[], maxNumberOfFields: number) => {
	const noOfExtraEmptyFields = maxNumberOfFields - (fields.length);
	let modifiedFields: Nullable<FieldVM>[] = [];

	if (noOfExtraEmptyFields) {
		const extraFields = new Array(noOfExtraEmptyFields).fill(null, 0, maxNumberOfFields);
		modifiedFields =
			fields
				? fields.concat(extraFields)
				: extraFields;
	} else {
		modifiedFields = fields;
	}

	return modifiedFields;
};

const WorkSummaryDetailsTableRow: React.FC<Props> = (props) => {
	const {
		data,
		maxNumberOfDefinitionFields,
		maxNumberOfInformationalFields,
		onBillingStatusToggle,
		onRowHighlight,
		highlightedGroup,
		cellWidthsMap,
		isReadOnly,
		hasPreviouslyBeenSplit,
		isGroupAndExcludeActionDisabled,
		isFirstOfItsGroupKey,
		isFirstOfItsGroupName,
		isLastOfItsGroupName,
		isOfCurrentlyHighlightedGroup,
		setNewWorkSummaryCheckedList,
		checkedWorkSummaryDetails,
		onGroupTogetherClick,
		onExcludeFromCalculationGroupClick,
		currentlyHighlightedGroupKey,
		onRestoreOriginalClick,
		jumpToQuantityFieldPathName,
		isAlternativeOption,
		isCustomSorted,
		timeZone,
	} = props;

	const {
		id,
		billableWorkId,
		date,
		workOrder,
		frSegment,
		frSegmentSecondary,
		definitionFields,
		workName,
		workType,
		quantity,
		unit,
		informationalFields,
		isBillable,
		workTypeFieldValueType,
		workTypeFieldType,
		workTypeFieldName,
		billingCodeCustomerId,
		billingCodeDescription,
		workSummaryGroup,
		subJobId,
		billingType,
		materialDisplayName,
		materialDescription,
	} = data;

	const [isBillingStatusToggleDisabled, setIsBillingStatusToggleDisabled] = React.useState(false);

	const EmptyCell = React.useMemo(() => <div className={styles['empty-cell']}>N/A</div>, []);

	const modifiedDefinitionFields = React.useMemo(
		() => _fillWithEmptyFields(definitionFields, maxNumberOfDefinitionFields),
		[definitionFields, maxNumberOfDefinitionFields]
	);
	const modifiedInformationalFields = React.useMemo(
		() => _fillWithEmptyFields(informationalFields ?? [], maxNumberOfInformationalFields),
		[informationalFields, maxNumberOfInformationalFields]
	);

	const groupToHighlight = React.useMemo(
		() => workSummaryGroup ?? resolveHighlightGroupKey({ billingType, billableWorkId, workType, definitionFields, subJobId }),
		[workSummaryGroup, billingType, billableWorkId, workType, definitionFields, subJobId]
	);

	const actionOptions = React.useMemo(() => {
		return [
			{ label: 'Restore original', onClick: onRestoreOriginalClick, disabled: (!hasPreviouslyBeenSplit || isReadOnly) },
			{ label: 'Group together', onClick: onGroupTogetherClick, disabled: (isGroupAndExcludeActionDisabled || isReadOnly) },
			{ label: 'Mark as Non-billable', onClick: onExcludeFromCalculationGroupClick, disabled: (isGroupAndExcludeActionDisabled || isReadOnly) },
		];
	}, [onRestoreOriginalClick, hasPreviouslyBeenSplit, isReadOnly, onGroupTogetherClick, isGroupAndExcludeActionDisabled, onExcludeFromCalculationGroupClick]);

	const cellClassName = bemElement('field-report__work-summary__table__row__data', 'cell');
	const cellTextClassName = bemElement(cellClassName, 'text');
	const billingStatusClassName = bemElement(cellClassName, 'billing-action', { billed: isBillable, readonly: isReadOnly || isBillingStatusToggleDisabled });
	const checkboxClassName = bemElement(cellClassName, 'checkbox');

	const _findWorkSummaryDetail = React.useCallback((_wsd) => _wsd.id === id, [id]);
	const isChecked = !!checkedWorkSummaryDetails.find(_findWorkSummaryDetail);

	const onRowClick = React.useCallback(() => {
		const _rowGroupKey = resolveHighlightGroupKey({ billingType, billableWorkId, workType, definitionFields, subJobId });
		onRowHighlight(groupToHighlight, _rowGroupKey);
		if (currentlyHighlightedGroupKey !== _rowGroupKey) {
			setNewWorkSummaryCheckedList([]);
		}
	}, [
		billableWorkId,
		billingType,
		currentlyHighlightedGroupKey,
		definitionFields,
		groupToHighlight,
		onRowHighlight,
		setNewWorkSummaryCheckedList,
		subJobId,
		workType,
	]);

	const handleCheckboxChange = React.useCallback(
		() => {
			if (isChecked) {
				const newCheckedList = Array.from(checkedWorkSummaryDetails);
				const currentCheckedIndex = newCheckedList.findIndex(_findWorkSummaryDetail);
				newCheckedList.splice(currentCheckedIndex, 1);
				setNewWorkSummaryCheckedList(newCheckedList);
			} else {
				setNewWorkSummaryCheckedList([...checkedWorkSummaryDetails, data]);
			}
		}
		, [isChecked, checkedWorkSummaryDetails, _findWorkSummaryDetail, setNewWorkSummaryCheckedList, data]);

	const handleBillingStatusToggle = React.useCallback(async () => {
		setIsBillingStatusToggleDisabled(true);
		await onBillingStatusToggle(data);
		setIsBillingStatusToggleDisabled(false);
	}, [onBillingStatusToggle, data]);

	const _renderInformationField = React.useCallback((_field: Nullable<FieldVM>, _index: number) => {
		const column = createIndexedColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.INFORMATIONAL_FIELD, _index);
		const resolvedUnit = _field?.fieldUnit ? resolveToLargerUnit(_field.fieldUnit) : '';

		const resolvedValue = _field ? resolveFieldValue(
			_field.value,
			_field.fieldType,
			_field.fieldUnit,
			timeZone
		) : null;
		const displayValue = _field && resolvedValue ? (
			<>
				{_field.name}: &nbsp;{resolvedValue} {resolvedUnit}
			</>
		) : (
			'N/A'
		);
		const key = `${_field?.id ?? `null-${_index}`}`;

		const tooltipMessage = _field ? displayValue : 'N/A';

		return (
			<WorkSummaryDetailsTableCell
				cellClassName={cellClassName}
				cellTextClassName={cellTextClassName}
				cellWidthsMap={cellWidthsMap}
				column={column}
				displayValue={displayValue}
				key={key}
				tooltipMessage={tooltipMessage}
			/>
		);
	}, [cellClassName, cellTextClassName, cellWidthsMap, timeZone]);

	const _renderDefinitionField = React.useCallback((_field: Nullable<FieldVM>, _index: number) => {
		const column = createIndexedColumn(WORK_SUMMARY_COLUMN_DEFINITION_MAP.DEFINITION_FIELD, _index);

		const resolvedUnit = _field?.fieldUnit ? resolveToLargerUnit(_field.fieldUnit) : '';
		const resolvedValue = _field ? resolveFieldValue(_field.value, _field.fieldType, _field.fieldUnit, timeZone) : null;
		const formattedDisplay = _field && resolvedValue ? (
			<>
				{_field.name}: &nbsp;{resolvedValue} {resolvedUnit}
			</>
		) : (
			'N/A'
		);
		const formattedTooltip = _field && resolvedValue ? formattedDisplay : 'N/A';

		const key = `${_field?.id ?? `null-${_index}`}`;

		return (
			<WorkSummaryDetailsTableCell
				cellClassName={cellClassName}
				cellTextClassName={cellTextClassName}
				cellWidthsMap={cellWidthsMap}
				column={column}
				displayValue={formattedDisplay}
				key={key}
				tooltipMessage={formattedTooltip}
			/>
		);
	}, [cellClassName, cellTextClassName, cellWidthsMap, timeZone]);

	const _renderMenuItem = React.useCallback((option) => {

		return (
			<span className={bemElement('field-report__work-summary__table__row__actions', 'menu-item')}>
				{option?.label}
			</span>
		);
	}, []);

	const isHighlighted = highlightedGroup === groupToHighlight;
	const showActionsButton = isOfCurrentlyHighlightedGroup && isFirstOfItsGroupKey && !isCustomSorted;
	const indicatePreviousSplit = isOfCurrentlyHighlightedGroup && hasPreviouslyBeenSplit;
	const showCheckbox = isOfCurrentlyHighlightedGroup && !isReadOnly && !isCustomSorted;

	const rowDataClassName = bemElement('field-report__work-summary__table__row', 'data', {
		highlighted: isHighlighted,
		split: indicatePreviousSplit,
		details: true,
		'last-seperated': isLastOfItsGroupName && !isCustomSorted,
		'first-seperated': isFirstOfItsGroupName && !isCustomSorted,
		'alternative': isAlternativeOption,
		'custom-sort': isCustomSorted,
	});

	let workTypeAdjustedName = <>{workTypeFieldName}</>;
	if (
		workTypeFieldValueType !== ReportBlockFieldEnum.ValueType.NUMBER
		&& workTypeFieldValueType !== ReportBlockFieldEnum.ValueType.CALCULATED
	) {
		const resolvedValue = resolveFieldValue(workType, workTypeFieldType, null, timeZone);
		workTypeAdjustedName =
			<>
				{workTypeFieldName}: &nbsp;{resolvedValue}
			</>;
	}

	return (
		<div className={bemBlock('field-report__work-summary__table__row', { 'custom-sort': isCustomSorted })}>
			<div className={bemElement('field-report__work-summary__table__row', 'actions', { highlighted: isHighlighted })}>
				{showActionsButton &&
					<Dropdown
						id="actions"
						isActionDropdown={true}
						labelKey="label"
						options={actionOptions}
						renderMenuItem={_renderMenuItem}
					/>
				}
			</div>
			<div
				className={rowDataClassName}
				onClick={onRowClick}
			>
				<span className={bemElement('field-report__work-summary__table__row__data', 'cell', { highlighted: isHighlighted, short: true })}>
					<span className={checkboxClassName}>
						{showCheckbox &&
							<Checkbox
								handleChange={handleCheckboxChange}
								isChecked={isChecked}
								isDisabled={isReadOnly}
							/>
						}
					</span>
					<BillingTypeIndicator billingType={billingType} className={styles.billingTypeIndicator} small />
					<span className={billingStatusClassName} onClick={!isBillingStatusToggleDisabled ? handleBillingStatusToggle : undefined}>
						<span className="icon-dollar" />
					</span>
					<a href={jumpToQuantityFieldPathName} rel="noreferrer" target="_blank">
						<span className="icon-external" />
					</a>
				</span>
				<WorkSummaryDetailsTableCell
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.DATE}
					displayValue={date}
					tooltipMessage={date}
				/>
				<WorkSummaryDetailsTableCell
					additionalStyling={{ long: true }}
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.WORK_ORDER}
					displayValue={workOrder}
					tooltipMessage={workOrder}
				/>
				<WorkSummaryDetailsTableCell
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.FR_SEGMENT}
					displayValue={frSegment}
					tooltipMessage={frSegment}
				/>
				<WorkSummaryDetailsTableCell
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.FR_SECONDARY_SEGMENT}
					displayValue={frSegmentSecondary ?? EmptyCell}
					tooltipMessage={frSegmentSecondary ?? 'N/A'}
				/>
				<WorkSummaryDetailsTableCell
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.WORK}
					displayValue={workName}
					tooltipMessage={workName}
				/>
				<WorkSummaryDetailsTableCell
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.TYPE}
					displayValue={workType ? workTypeAdjustedName : EmptyCell}
					tooltipMessage={workType ? workTypeAdjustedName : 'N/A'}
				/>
				{modifiedDefinitionFields?.map(_renderDefinitionField)}
				<WorkSummaryDetailsTableCell
					additionalStyling={{ 'align-right': true, short: true }}
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.QUANTITY}
					displayValue={(quantity ?? 0).toString()}
					tooltipMessage={(quantity ?? 0).toString()}
				/>
				<WorkSummaryDetailsTableCell
					additionalStyling={{ short: true }}
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.UNIT}
					displayValue={unit && QuantityUnitMap[unit] ? QuantityUnitMap[unit].replace('_', '/') : 'N/A'}
					tooltipMessage={unit && QuantityUnitLabel[unit] ? QuantityUnitLabel[unit] : 'N/A'}
				/>
				{modifiedInformationalFields?.map(_renderInformationField)}
				<WorkSummaryDetailsTableCell
					additionalStyling={{ short: true }}
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.BILLING_CODE_ID}
					displayValue={billingCodeCustomerId ?? EmptyCell}
					tooltipMessage={billingCodeCustomerId ?? 'N/A'}
				/>
				<WorkSummaryDetailsTableCell
					additionalStyling={{ short: true }}
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.MATERIAL_CODE_ID}
					displayValue={materialDisplayName ?? EmptyCell}
					tooltipMessage={materialDisplayName ?? 'N/A'}
				/>
				<WorkSummaryDetailsTableCell
					additionalStyling={{ short: true }}
					cellClassName={cellClassName}
					cellTextClassName={cellTextClassName}
					cellWidthsMap={cellWidthsMap}
					column={WORK_SUMMARY_COLUMN_DEFINITION_MAP.CODE_DESCRIPTION}
					displayValue={billingCodeDescription ?? materialDescription ?? EmptyCell}
					tooltipMessage={billingCodeDescription ?? materialDescription ?? 'N/A'}
				/>
			</div>
		</div>
	);
};

export default WorkSummaryDetailsTableRow;
